import { useEffect, useMemo, useState } from 'react';
import { Cpu, LayoutGrid, List } from 'lucide-react';
import { useDevices } from '@/hooks/useDevices';
import { useAuth } from '@/hooks/useAuth';
import { DeviceCard } from '@/components/devices/DeviceCard';
import { DeviceTable, type SortState } from '@/components/devices/DeviceTable';
import { DeviceFilters, type DeviceFilter } from '@/components/devices/DeviceFilters';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Pagination } from '@/components/ui/Pagination';
import { expiryStatus } from '@/lib/license';
import { cn } from '@/lib/cn';
import { parseServerTime } from '@/lib/format';
import type { Device } from '@/lib/types';

type Layout = 'table' | 'tiles';

const PAGE_SIZES = [25, 50, 100];
const LAYOUT_KEY = 'samvidhx.devices.layout';
const PAGE_SIZE_KEY = 'samvidhx.devices.pageSize';

// Per-browser view preferences; storage can be unavailable (private mode), so
// every access is guarded and falls back to the defaults.
function readPref(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function writePref(key: string, value: string) {
  try { localStorage.setItem(key, value); } catch { /* ignore */ }
}

function lastSeenMs(d: Device): number {
  if (!d.last_seen) return 0;
  const t = parseServerTime(d.last_seen);
  return Number.isNaN(t) ? 0 : t;
}

function compare(a: Device, b: Device, key: SortState['key']): number {
  switch (key) {
    case 'status':    return Number(!!b.online) - Number(!!a.online);   // online first
    case 'license': {
      // Soonest-to-expire first; devices without an expiry go last.
      const da = expiryStatus(a.expires_at).daysLeft ?? Infinity;
      const db = expiryStatus(b.expires_at).daysLeft ?? Infinity;
      return da === db ? 0 : da < db ? -1 : 1;
    }
    case 'last_seen': return lastSeenMs(b) - lastSeenMs(a);             // most recent first
    default:          return 0;
  }
}

export function DevicesPage() {
  const { data, isLoading } = useDevices();
  const { isAdminRole } = useAuth();
  const [query, setQuery]   = useState('');
  const [filter, setFilter] = useState<DeviceFilter>('all');
  const [sort, setSort]     = useState<SortState>({ key: 'name', dir: 'asc' });
  const [page, setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const n = Number(readPref(PAGE_SIZE_KEY));
    return PAGE_SIZES.includes(n) ? n : PAGE_SIZES[0];
  });
  const [layoutPref, setLayoutPref] = useState<Layout | null>(() => {
    const v = readPref(LAYOUT_KEY);
    return v === 'table' || v === 'tiles' ? v : null;
  });

  // Superadmin/admin manage many devices → dense table by default. Operators
  // and viewers keep the familiar card list unless they pick otherwise.
  const layout: Layout = layoutPref ?? (isAdminRole ? 'table' : 'tiles');

  const devices = data ?? [];

  const counts = useMemo(() => ({
    all:      devices.length,
    online:   devices.filter((d) => !!d.online).length,
    offline:  devices.filter((d) => !d.online).length,
    expiring: devices.filter((d) => {
      const k = expiryStatus(d.expires_at).kind;
      return k === 'warn' || k === 'expired';
    }).length,
  }), [devices]);

  const filtered = useMemo(() => {
    let list = devices;
    if (filter === 'online')   list = list.filter((d) => !!d.online);
    if (filter === 'offline')  list = list.filter((d) => !d.online);
    if (filter === 'expiring') list = list.filter((d) => {
      const k = expiryStatus(d.expires_at).kind;
      return k === 'warn' || k === 'expired';
    });
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((d) =>
        d.client_name.toLowerCase().includes(q) ||
        d.client_id.toLowerCase().includes(q),
      );
    }
    const byName = (a: Device, b: Device) =>
      a.client_name.localeCompare(b.client_name, undefined, { numeric: true, sensitivity: 'base' });
    const dir = sort.dir === 'asc' ? 1 : -1;
    // Name is the tiebreaker for every other key so pages stay stable while
    // WS pushes flip online state.
    return [...list].sort((a, b) =>
      (sort.key === 'name' ? byName(a, b) : compare(a, b, sort.key)) * dir || byName(a, b));
  }, [devices, filter, query, sort]);

  // Back to page 1 whenever the result set is redefined.
  useEffect(() => { setPage(1); }, [filter, query, sort, pageSize]);

  // Keep the page in range if devices disappear (deleted, filter counts change).
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const current = Math.min(page, pages);
  const visible = filtered.slice((current - 1) * pageSize, current * pageSize);

  const chooseLayout = (l: Layout) => { setLayoutPref(l); writePref(LAYOUT_KEY, l); };
  const choosePageSize = (n: number) => { setPageSize(n); writePref(PAGE_SIZE_KEY, String(n)); };
  const goTo = (p: number) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  return (
    <div>
      <div className="flex items-baseline justify-between mb-5 gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-ink tracking-tight">Devices</h1>
          <p className="text-sm text-slate-500 mt-0.5">Live status of every controller assigned to you.</p>
        </div>
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1 shrink-0" role="group" aria-label="Layout">
          {([['table', List, 'Table'], ['tiles', LayoutGrid, 'Tiles']] as const).map(([id, Icon, label]) => (
            <button key={id} onClick={() => chooseLayout(id)} aria-pressed={layout === id} title={label}
              className={cn('flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-colors',
                layout === id ? 'bg-ink text-white' : 'text-slate-600 hover:bg-slate-100')}>
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>
      </div>

      <DeviceFilters
        query={query} onQueryChange={setQuery}
        filter={filter} onFilterChange={setFilter}
        counts={counts}
      />

      {isLoading && (
        <div className="grid gap-3">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}
        </div>
      )}

      {!isLoading && filtered.length === 0 && (
        <EmptyState
          icon={<Cpu className="w-7 h-7" />}
          title={devices.length === 0 ? 'No devices assigned' : 'No matches'}
          description={devices.length === 0
            ? 'Devices will appear here once they connect to the server. Contact your administrator if you expected one.'
            : 'Try adjusting the filter or search.'}
        />
      )}

      {!isLoading && filtered.length > 0 && (
        <>
          {layout === 'table' ? (
            <DeviceTable devices={visible} sort={sort} onSortChange={setSort} />
          ) : (
            <>
              <div className="flex justify-end mb-2">
                <select
                  value={`${sort.key}:${sort.dir}`}
                  onChange={(e) => {
                    const [key, dir] = e.target.value.split(':') as [SortState['key'], SortState['dir']];
                    setSort({ key, dir });
                  }}
                  className="h-8 px-2 rounded-md border border-slate-300 bg-white text-xs text-ink"
                  aria-label="Sort devices"
                >
                  <option value="name:asc">Name A–Z</option>
                  <option value="name:desc">Name Z–A</option>
                  <option value="status:asc">Online first</option>
                  <option value="status:desc">Offline first</option>
                  <option value="license:asc">License expiring soonest</option>
                  <option value="last_seen:asc">Recently seen</option>
                </select>
              </div>
              <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-3">
                {visible.map((d, i) => (
                  <DeviceCard key={d.client_id} device={d} index={i} />
                ))}
              </div>
            </>
          )}

          <Pagination
            page={current}
            pageSize={pageSize}
            total={filtered.length}
            onPageChange={goTo}
            pageSizeOptions={PAGE_SIZES}
            onPageSizeChange={choosePageSize}
          />
        </>
      )}
    </div>
  );
}
